import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import { Plus, Trash2, Users, Mail, Calendar, Shield, CheckCircle, Clock, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  pending: { label: 'Not Yet Asked', icon: Clock, color: 'bg-gray-50 text-gray-600 border-gray-200' },
  requested: { label: 'Requested', icon: AlertCircle, color: 'bg-amber-50 text-amber-700 border-amber-200' },
  in_progress: { label: 'In Progress', icon: Clock, color: 'bg-blue-50 text-blue-700 border-blue-200' },
  submitted: { label: 'Submitted', icon: CheckCircle, color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  declined: { label: 'Declined', icon: AlertCircle, color: 'bg-red-50 text-red-700 border-red-200' },
};

const REC_TYPES = {
  counselor: { label: 'School Counselor', description: 'Required by most schools. Provides school context and overall recommendation.' },
  teacher: { label: 'Teacher', description: 'Most schools require 1-2 teacher recommendations from core academic subjects.' },
  other: { label: 'Other Recommender', description: 'Optional — coaches, mentors, employers, or other adults who know you well.' },
};

function RecCard({ rec, onStatusChange, onDelete, colleges }) {
  const [expanded, setExpanded] = useState(false);
  const status = STATUS_CONFIG[rec.status] || STATUS_CONFIG.pending;
  const StatusIcon = status.icon;

  return (
    <Card className={`border transition-all ${expanded ? 'border-[#0F392B]/30 shadow-sm' : 'border-[#E2E2E2]'}`}
      data-testid={`rec-card-${rec.id}`}>
      <div className="flex items-center gap-3 px-5 py-3.5 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${rec.rec_type === 'counselor' ? 'bg-purple-50' : rec.rec_type === 'teacher' ? 'bg-blue-50' : 'bg-amber-50'}`}>
          <Users className={`h-4 w-4 ${rec.rec_type === 'counselor' ? 'text-purple-600' : rec.rec_type === 'teacher' ? 'text-blue-600' : 'text-amber-600'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium truncate">{rec.name}</h3>
            <Badge variant="outline" className={`text-[10px] ${status.color}`}>
              <StatusIcon className="h-3 w-3 mr-0.5" />{status.label}
            </Badge>
          </div>
          <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground">
            <span className="capitalize">{rec.rec_type || rec.role}</span>
            {rec.subject && <span>· {rec.subject}</span>}
            {rec.email && (
              <span className="flex items-center gap-0.5">
                · <Mail className="h-3 w-3" /> {rec.email}
              </span>
            )}
          </div>
        </div>
        {rec.waiver_signed && (
          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[9px]">
            <Shield className="h-3 w-3 mr-0.5" />FERPA
          </Badge>
        )}
        {expanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
      </div>

      {expanded && (
        <CardContent className="px-5 pb-5 pt-0 space-y-4 border-t border-[#E2E2E2]">
          <div className="pt-4 grid grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">Status</p>
              <Select value={rec.status || 'pending'} onValueChange={v => onStatusChange(rec.id, v)}>
                <SelectTrigger className="h-8 text-xs" data-testid={`rec-status-${rec.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {rec.due_date && (
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">Due Date</p>
                <div className="flex items-center gap-1.5 text-xs">
                  <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                  {new Date(rec.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </div>
              </div>
            )}
          </div>

          {rec.relationship && (
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">Relationship</p>
              <p className="text-xs">{rec.relationship} {rec.relationship_duration ? `(${rec.relationship_duration})` : ''}</p>
            </div>
          )}

          {rec.schools && rec.schools.length > 0 && (
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">Schools Assigned</p>
              <div className="flex flex-wrap gap-1">
                {rec.schools.map((s, i) => (
                  <Badge key={i} variant="outline" className="text-[10px]">{s}</Badge>
                ))}
              </div>
            </div>
          )}

          {rec.notes && (
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">Notes</p>
              <p className="text-xs text-muted-foreground">{rec.notes}</p>
            </div>
          )}

          <div className="flex justify-end pt-2 border-t border-[#E2E2E2]">
            <Button size="sm" variant="ghost" onClick={() => onDelete(rec.id, rec.name)}
              className="text-xs text-red-500 hover:text-red-700 hover:bg-red-50 h-8"
              data-testid={`delete-rec-${rec.id}`}>
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
}

export default function RecommendersPage() {
  const [recs, setRecs] = useState([]);
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', rec_type: 'teacher', subject: '',
    relationship: '', relationship_duration: '', schools: [],
    waiver_signed: false, due_date: '', notes: '',
  });

  const load = useCallback(() => {
    Promise.all([
      api.getRecommenders(),
      api.getColleges().catch(() => []),
    ]).then(([r, c]) => { setRecs(r); setColleges(c); })
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.createRecommender({
        ...form,
        role: form.rec_type,
        schools: form.schools.length > 0 ? form.schools : null,
      });
      toast.success('Recommender added');
      setForm({ name: '', email: '', rec_type: 'teacher', subject: '', relationship: '', relationship_duration: '', schools: [], waiver_signed: false, due_date: '', notes: '' });
      setOpen(false);
      load();
    } catch (err) { toast.error(err.message); }
  };

  const handleStatusChange = async (id, status) => {
    try { await api.updateRecommender(id, { status }); load(); } catch (err) { toast.error(err.message); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Remove ${name}?`)) return;
    try { await api.deleteRecommender(id); toast.success('Removed'); load(); } catch (err) { toast.error(err.message); }
  };

  const toggleSchool = (school) => {
    setForm(prev => ({
      ...prev,
      schools: prev.schools.includes(school)
        ? prev.schools.filter(s => s !== school)
        : [...prev.schools, school],
    }));
  };

  const counselors = recs.filter(r => (r.rec_type || r.role) === 'counselor');
  const teachers = recs.filter(r => (r.rec_type || r.role) === 'teacher');
  const others = recs.filter(r => !['counselor', 'teacher'].includes(r.rec_type || r.role));

  const submittedCount = recs.filter(r => r.status === 'submitted').length;

  if (loading) return (
    <div className="max-w-3xl mx-auto space-y-3 animate-pulse">
      {[1,2,3].map(i => <div key={i} className="h-16 bg-[#E2E2E2] rounded-xl" />)}
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="recommenders-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-medium tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            Recommendations
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            {recs.length} recommenders · {submittedCount} submitted
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#0F392B] hover:bg-[#0F392B]/90 text-white text-xs h-8" data-testid="add-recommender-btn">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add Recommender
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "'Playfair Display', serif" }}>Add Recommender</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs">Recommender Type</Label>
                <Select value={form.rec_type} onValueChange={v => setForm({ ...form, rec_type: v })}>
                  <SelectTrigger data-testid="rec-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(REC_TYPES).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground">{REC_TYPES[form.rec_type]?.description}</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Full Name *</Label>
                  <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                    placeholder="Dr. Jane Smith" required data-testid="rec-name-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Email *</Label>
                  <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                    placeholder="teacher@school.edu" required data-testid="rec-email-input" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Subject / Department</Label>
                  <Input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })}
                    placeholder="e.g., AP Physics" data-testid="rec-subject-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Due Date</Label>
                  <Input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })}
                    data-testid="rec-due-date-input" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Relationship</Label>
                  <Input value={form.relationship} onChange={e => setForm({ ...form, relationship: e.target.value })}
                    placeholder="e.g., AP Physics teacher" data-testid="rec-relationship-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">How Long?</Label>
                  <Input value={form.relationship_duration} onChange={e => setForm({ ...form, relationship_duration: e.target.value })}
                    placeholder="e.g., 2 years" data-testid="rec-duration-input" />
                </div>
              </div>

              {colleges.length > 0 && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Assign to Schools</Label>
                  <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                    {colleges.map(c => (
                      <button key={c.id} type="button" onClick={() => toggleSchool(c.name)}
                        className={`px-2.5 py-1 text-[10px] rounded-md border transition-all ${form.schools.includes(c.name) ? 'bg-[#0F392B] text-white border-[#0F392B]' : 'bg-white text-muted-foreground border-[#E2E2E2] hover:border-[#0F392B]/30'}`}
                        data-testid={`rec-school-${c.name}`}>
                        {c.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer" data-testid="rec-waiver-check">
                  <input type="checkbox" checked={form.waiver_signed}
                    onChange={e => setForm({ ...form, waiver_signed: e.target.checked })}
                    className="rounded border-[#E2E2E2] text-[#0F392B] focus:ring-[#0F392B]" />
                  <span className="text-xs text-muted-foreground">FERPA waiver signed</span>
                </label>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">Notes (optional)</Label>
                <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                  placeholder="Any notes about this recommender..." rows={2} data-testid="rec-notes-input" />
              </div>

              <Button type="submit" className="w-full bg-[#0F392B] hover:bg-[#0F392B]/90 text-white" data-testid="rec-submit-btn">
                Add Recommender
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Progress */}
      <div className="h-1.5 bg-[#F2F2F0] rounded-full overflow-hidden">
        <div className="h-full bg-[#0F392B] rounded-full transition-all duration-500"
          style={{ width: `${recs.length > 0 ? (submittedCount / recs.length) * 100 : 0}%` }} />
      </div>

      {recs.length === 0 ? (
        <div className="text-center py-16">
          <Users className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-muted-foreground text-sm">No recommenders yet</p>
          <p className="text-xs text-muted-foreground mt-1">
            Most schools require 1 counselor + 1-2 teacher recommendations
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Counselor Section */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2 h-2 rounded-full bg-purple-400" />
              <h2 className="text-sm font-semibold">School Counselor ({counselors.length})</h2>
              {counselors.length === 0 && <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[9px]">Required</Badge>}
            </div>
            {counselors.length === 0 ? (
              <p className="text-xs text-muted-foreground pl-4 italic">No counselor added yet. Most schools require one.</p>
            ) : (
              <div className="space-y-2">
                {counselors.map(r => <RecCard key={r.id} rec={r} onStatusChange={handleStatusChange} onDelete={handleDelete} colleges={colleges} />)}
              </div>
            )}
          </div>

          {/* Teacher Section */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2 h-2 rounded-full bg-blue-400" />
              <h2 className="text-sm font-semibold">Teacher Recommendations ({teachers.length})</h2>
              {teachers.length < 2 && <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[9px]">Need {2 - teachers.length} more</Badge>}
            </div>
            {teachers.length === 0 ? (
              <p className="text-xs text-muted-foreground pl-4 italic">Add 1-2 teachers from core academic subjects (STEM + Humanities).</p>
            ) : (
              <div className="space-y-2">
                {teachers.map(r => <RecCard key={r.id} rec={r} onStatusChange={handleStatusChange} onDelete={handleDelete} colleges={colleges} />)}
              </div>
            )}
          </div>

          {/* Other Section */}
          {others.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-2 h-2 rounded-full bg-amber-400" />
                <h2 className="text-sm font-semibold">Other Recommenders ({others.length})</h2>
              </div>
              <div className="space-y-2">
                {others.map(r => <RecCard key={r.id} rec={r} onStatusChange={handleStatusChange} onDelete={handleDelete} colleges={colleges} />)}
              </div>
            </div>
          )}
        </div>
      )}

      <p className="text-xs text-muted-foreground text-center">
        Tip: Ask recommenders at least 4 weeks before the earliest deadline. Sign the FERPA waiver to show colleges you trust your recommenders.
      </p>
    </div>
  );
}
